import type { FC } from 'react';
import React, { memo, useCallback, useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';
import memoizeOne from 'memoize-one';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { ExtensionParams } from '@atlaskit/editor-common/extensions/extension-handler';
import ProviderFactory from '@atlaskit/editor-common/provider-factory/provider-factory';
import type { EditorContentMode } from '@atlaskit/editor-common/types/editor-appearance';
import type { ADNode } from '@atlaskit/editor-common/utils/validator';

import { useExternalLinkHandlers } from '@confluence/adf-renderer/entry-points/useExternalLinkHandlers.ts';
import { useRendererAnnotationProviderOptions } from '@confluence/annotation-provider/entry-points/useRendererAnnotationProviderOptions.ts';
import { RendererExtensionContext } from '@confluence/content-renderer-extension-context/entry-points/RendererExtensionContext.ts';
import {
	NBMRendererContent,
	resolveNBMRenderConfig,
} from '@confluence/content-renderer-legacy-macros/entry-points/NBMRenderer.ts';
import { NestedLcmHierarchyContext } from '@confluence/content-renderer-legacy-macros/entry-points/NestedLcmHierarchyContext.ts';
import { useLegacyContentMacroRendererAnalytics } from '@confluence/content-renderer-legacy-macros/entry-points/useLegacyContentMacroRendererAnalytics.ts';
import { useNestedLcmProviderFactory } from '@confluence/content-renderer-legacy-macros/entry-points/useNestedLcmProviderFactory.ts';
import { useEditorContentModeValue } from '@confluence/editor-content-mode-button/entry-points/useEditorContentModeValue.ts';
import { useContentType, usePageContentId } from '@confluence/page-context';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId.ts';
import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey.ts';

import type { LegacyContentMacroParams } from './LegacyContentMacroParams.ts';

export interface NBMRendererClientProps {
	contentMode?: EditorContentMode;
	node: ExtensionParams<LegacyContentMacroParams>;
}

export const NBMRendererClient: FC<NBMRendererClientProps> = memo((props) => {
	const { contentMode, node } = props;
	// Inside a bridged recursive-SSR subtree this LCM is already within a renderer that holds the
	// manifests, so it inherits them instead of building and populating a factory of its own. On a
	// client-only page or an SPA transition there is nothing to inherit and it keeps its own.
	const insideNestedLcmHierarchy = useContext(NestedLcmHierarchyContext);
	const spaceId = useSpaceId();
	const spaceKey = useSpaceKey();
	const [contentId] = usePageContentId();
	const [contentType = 'page'] = useContentType();
	const { environment, cloudId, userId, activationId } = useSessionData();
	const { getExtensionHandlers, populateExtensionProviders } = useContext(RendererExtensionContext);
	// A child of the ambient factory rather than the factory itself: `mergeDataProviders` mutates
	// whatever it is given, so reusing the page factory directly would swap the page's own
	// media/card/task providers and notify every subscriber on the page.
	const derivedProviderFactory = useNestedLcmProviderFactory();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const intl = useIntl();
	const { mode: editorContentMode } = useEditorContentModeValue({ contentId });
	const effectiveContentMode = contentMode ?? editorContentMode;

	const { adfDocument, appearance } = resolveNBMRenderConfig(node);

	/**
	 * Both context functions use classic renderer types that are currently exposed as `any`.
	 */
	const extensionHandlers = useMemo(
		() =>
			getExtensionHandlers?.(
				{
					adf: adfDocument as ADNode,
					contentId,
					spaceKey,
					contentType,
					appearance: 'full-width',
					referentialityDataSourceProvider: {},
				},
				createAnalyticsEvent,
			),
		[adfDocument, contentId, contentType, createAnalyticsEvent, getExtensionHandlers, spaceKey],
	);

	const annotationProvider = useRendererAnnotationProviderOptions({
		pageId: contentId ?? '',
		validAnnotationsCallback: () => {},
		triggerAnalyticsEvent: createAnalyticsEvent,
		updateDocument: () => {},
		isArchived: false,
	});

	const getDataProviders = useCallback(() => {
		const providerFactory = ProviderFactory.create({});

		if (populateExtensionProviders) {
			void populateExtensionProviders(providerFactory, {
				activationId,
				accountId: userId,
				cloudId,
				containerId: spaceKey,
				contentId,
				contentType,
				createAnalyticsEvent,
				environment,
				extensionHandlers,
				intl,
				spaceKey,
				spaceId,
			});
		}

		return providerFactory;
	}, [
		populateExtensionProviders,
		activationId,
		userId,
		cloudId,
		spaceKey,
		contentId,
		contentType,
		createAnalyticsEvent,
		environment,
		extensionHandlers,
		intl,
		spaceId,
	]);
	// Keep the factory stable until its content or provider configuration changes.
	// Cache the getter lazily so the inherited path never populates an unused factory.
	const getMemoizedDataProviders = useMemo(() => memoizeOne(getDataProviders), [getDataProviders]);

	const { eventHandlers, onSetLinkTarget } = useExternalLinkHandlers();

	useLegacyContentMacroRendererAnalytics({ adfDocument, contentId });

	return (
		<NBMRendererContent
			allowAnnotations
			annotationProvider={annotationProvider}
			appearance={appearance}
			contentId={contentId}
			contentMode={effectiveContentMode}
			dataProviders={insideNestedLcmHierarchy ? derivedProviderFactory : getMemoizedDataProviders()}
			adfDocument={adfDocument}
			eventHandlers={eventHandlers}
			onSetLinkTarget={onSetLinkTarget}
		/>
	);
});
